import { useGetSignalList } from './hooks/useGetSignalList';
import SignalCard from './components/SignalCard';
import { Skeleton } from '@/components/ui/skeleton';
import SettingSheet from './components/SettingSheet';
import NoData from '@/widget/NoData';
import { useSignalHoldingList } from './hooks/useSignalHoldingList';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef } from 'react';
import { useTailwindRealHeight } from '@/hooks/base/useTailwindRealHeight';
import { useDynamicHeight } from '@/hooks/base/useDynamicHeight';
import FilterSheet from './components/FilterSheet';
import HowWorkBtn from '@/widget/HowWorkBtn';
import SearchController from './components/SearchController';

export default () => {
  const { loading, renderList, keyword, onInputChange, clearKeyword } = useGetSignalList();
  useSignalHoldingList();
  const cardDesignHeight = 360;
  const tailwindRealHeight = useTailwindRealHeight(cardDesignHeight);
  const gapHeight = useTailwindRealHeight(16);
  const viewHeight = useDynamicHeight({ customHeight: 62 });
  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: renderList?.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => tailwindRealHeight + gapHeight,
    overscan: 5,
  });

  useEffect(() => {
    if (parentRef.current) {
      rowVirtualizer.measure();
    }
  }, [gapHeight, viewHeight, tailwindRealHeight, parentRef.current]);

  return (
    <div className="px-[0]">
      <div className="h-[62px] flex items-center gap-x-[8px] sticky top-0 bg-primaryBgColor z-10">
        <SearchController value={keyword} onInputChange={onInputChange} clearKeyword={clearKeyword} />
        <FilterSheet />
        <SettingSheet />
        <HowWorkBtn />
      </div>
      {loading ? (
        <div className="flex flex-col gap-y-[16px]">
          {[...new Array(5)].map((_, index) => {
            return <Skeleton key={index} className="h-[360px] rounded-xl" />;
          })}
        </div>
      ) : renderList.length > 0 ? (
        <div
          ref={parentRef}
          style={{
            height: viewHeight + 'px',
            overflow: 'auto',
          }}
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => (
              <div
                key={virtualRow.index}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: `${tailwindRealHeight}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <SignalCard data={renderList[virtualRow.index]} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <NoData />
      )}
    </div>
  );
};
